import { useEffect, useRef, useState } from 'react';
import { ConfigProvider, Modal } from 'antd';
import SweetAlert from '@components/Feedback/SweetAlert/Index';
import { DeletePlanoAcao, SavePlanoAcaoSharepoint } from '@services/PlanoAcao/Index';
import ButtonComp from '@components/UI/Button/Index';
import { StyledTable } from '@styles/datatables/datatable-antd.js';
import ptBR from 'antd/es/locale/pt_BR';
import { readFileAsBase64 } from '@components/Modals/Shared/UploadFileModal/ReadFile';
import { v4 as uuidv4 } from 'uuid';
import { WarningOptionComp } from '@components/UI/WarningOption/Index.jsx';
import { Spinner } from '@styles/loading.js';
import { allowedExtensions } from '@components/Modals/Shared/UploadFileModal/FileExtensions';
import { useDispatch, useSelector } from 'react-redux';
import { makeSelectPlanosAcaoByIdProcessoAndIdUnidadeGerencia } from '@store/selectors/planosAcaoSelector';
import { atualizarPlanosAcao, carregarPlanosAcao } from '@store/slices/planosAcaoSlice';
import { useAppAuth } from '@contexts/AuthContext';

const PlanoAcaoAnexoModal = ({
    visible,
    closeModal,
    idSolicitacao,
    idProcesso,
    idUnidadeGerencia,
    codigoProcesso,
    planosAcaoExistentes
}) => {
    const dispatch = useDispatch();
    // memoize the selector instance
    const selector = makeSelectPlanosAcaoByIdProcessoAndIdUnidadeGerencia(
        idProcesso,
        idUnidadeGerencia
    );
    const key = `${idProcesso}-${idUnidadeGerencia}`;
    const documentosState = useSelector(selector);
    const fileInputRef = useRef(null);
    const MAX_FILE_SIZE = 50 * 1024 * 1024;

    const [loading, setLoading] = useState(false);
    const [localFiles, setLocalFiles] = useState({});

    const { nome } = useAppAuth();

    useEffect(() => {
        dispatch(carregarPlanosAcao({ key: key, planosAcao: planosAcaoExistentes || [] }));
    }, [dispatch, key, planosAcaoExistentes]);

    const handleDeleteFile = async (file) => {
        try {
            const confirmDelete = await SweetAlert.delete(
                `Esta ação será irreversível e excluirá o seguinte plano de ação: <strong>${file.nomeDocumento}</strong>`
            );
            if (!confirmDelete) return;

            if (file.id) {
                SweetAlert.toastLoading();
                await DeletePlanoAcao(file.id);
            }

            const updatedDocuments = documentosState.filter((doc) =>
                file.id ? doc.id !== file.id : doc.tempId !== file.tempId
            );

            const { [file.tempId]: _, ...newLocalFiles } = localFiles;
            setLocalFiles(newLocalFiles);

            dispatch(atualizarPlanosAcao({ key: key, planosAcao: updatedDocuments }));

            SweetAlert.toastSuccess('Plano de ação excluído!');
        } catch (error) {
            console.error(error);
            SweetAlert.toastError(`Erro ao remover o plano de ação ${file.nomeDocumento}.`);
        }
    };

    const handleFileAction = (file) => {
        if (file?.urlDocumentoSharepoint) {
            try {
                window.open(file.urlDocumentoSharepoint, '_blank');
            } catch (error) {
                console.error('Erro ao abrir o documento:', error);
                SweetAlert.toastError(`Erro ao abrir o documento ${file.nomeDocumento}.`);
            }
        }
    };

    const handleAddFiles = async (e) => {
        const files = Array.from(e.target.files);
        const updatedDocuments = [...documentosState];
        const newLocalFiles = { ...localFiles };

        for (const file of files) {
            if (file.size > MAX_FILE_SIZE) {
                SweetAlert.toastError(`Arquivo muito grande: "${file.name}" excede 50MB.`);
                continue;
            }

            if (!allowedExtensions.includes(file.type)) {
                SweetAlert.toastError(`Extensão não permitida: "${file.name}".`);
                continue;
            }

            const alreadyExists = updatedDocuments.some(doc => doc.nomeDocumento === file.name);

            if (alreadyExists) {
                SweetAlert.toastInfo(`O arquivo "${file.name}" já foi adicionado.`);
                continue;
            }

            const base64 = (await readFileAsBase64(file)).split(",")[1];
            const tempId = uuidv4();

            updatedDocuments.push({
                tempId,
                nomeDocumento: file.name,
                tamanhoArquivoEmMB: parseFloat((file.size / (1024 * 1024)).toFixed(4)),
                dataUpload: new Date().toISOString(),
                nomeUsuario: nome ?? '-',
                isNew: true,
            });

            newLocalFiles[tempId] = { file, base64 };
        }

        dispatch(atualizarPlanosAcao({ key: key, planosAcao: updatedDocuments }));
        setLocalFiles(newLocalFiles);
        e.target.value = '';
    };

    const openFileDialog = () => fileInputRef.current?.click();

    const handleClearFiles = () => {
        const existingOnly = documentosState.filter(
            (doc) => planosAcaoExistentes.some((eDoc) => eDoc.id === doc.id)
        );

        if (existingOnly.length === documentosState.length) {
            SweetAlert.toastInfo("Nenhum arquivo novo carregado.");
        }

        dispatch(atualizarPlanosAcao({ key: key, planosAcao: existingOnly }));
    };

    const sendNewFilesToAPI = async () => {
        const newDocs = documentosState.filter(
            (doc) => !planosAcaoExistentes.some((eDoc) => eDoc.id === doc.id)
        );
        const updatedDocuments = [...documentosState];
        let hasError = false;

        for (const doc of newDocs) {
            const fileData = localFiles[doc.tempId];
            if (!fileData) continue;

            const formData = new FormData();

            formData.append('IdProcesso', idProcesso);
            formData.append('IdUnidadeGerencia', idUnidadeGerencia);
            formData.append('CodigoProcesso', codigoProcesso);
            formData.append('IdSolicitacao', idSolicitacao);
            formData.append('Arquivo', fileData.file);

            try {
                const savedDocument = await SavePlanoAcaoSharepoint(formData);
                const index = updatedDocuments.findIndex((d) => d.tempId === doc.tempId);
                if (index !== -1) {
                    updatedDocuments[index] = { ...savedDocument, isNew: false };
                    delete localFiles[doc.tempId];
                }
            } catch (error) {
                hasError = true;
                SweetAlert.toastError(`Erro ao enviar o plano de ação ${doc.nomeDocumento}.`);
            }
        }

        dispatch(atualizarPlanosAcao({ key: key, planosAcao: updatedDocuments }));
        return !hasError;
    };

    const handleSubmit = async () => {
        try {
            const newDocs = documentosState.filter((doc) => !planosAcaoExistentes.some((eDoc) => eDoc.id === doc.id));

            if (newDocs.length === 0) {
                SweetAlert.toastInfo("Nenhum arquivo novo para salvar.");
                return;
            }
            setLoading(true);
            SweetAlert.toastLoading();

            const success = await sendNewFilesToAPI();

            if (success) {
                SweetAlert.toastSuccess("Plano de ação anexado com sucesso!")
            } else {
                SweetAlert.toastError(
                    "Alguns documentos não foram salvos corretamente."
                );
            }
        } catch (error) {
            console.error(error)
            SweetAlert.toastError("Erro ao processar os planos de ação.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        closeModal();
    };

    const columns = [
        {
            title: '#',
            render: (text, record, index) => index + 1,
            width: '10%',
            key: 'index'
        },
        {
            title: 'NOME DO ARQUIVO',
            dataIndex: 'nomeDocumento',
            key: 'nomeDocumento',
            width: '30%',
            render: (text, record) => {
                const isSaved = !!record.urlDocumentoSharepoint || !!record.id;
                return (
                    <span
                        style={{
                            textDecoration: isSaved ? 'underline' : 'none',
                            cursor: isSaved ? 'pointer' : 'not-allowed',
                            fontWeight: isSaved ? 'bold' : 'normal',
                            color: isSaved ? 'inherit' : 'gray',
                            textAlign: 'start',
                            display: 'block'
                        }}
                        onClick={() => {
                            if (isSaved) handleFileAction(record);
                            else SweetAlert.toastError('Salve o relatório antes de visualizá-lo.');
                        }}
                    >
                        {text}
                    </span>
                );
            }
        },
        {
            title: 'DATA DE UPLOAD',
            dataIndex: 'dataUpload',
            key: 'dataUpload',
            width: '20%',
            render: (date) => new Date(date).toLocaleString('pt-BR')
        },
        {
            title: 'USUÁRIO',
            dataIndex: 'nomeUsuario',
            key: 'nomeUsuario',
            width: '15%'
        },
        {
            title: 'TAMANHO',
            dataIndex: 'tamanhoArquivoEmMB',
            key: 'tamanhoArquivoEmMB',
            width: '15%',
            render: (size) => `${parseFloat(size).toFixed(4)} MB`
        },
        {
            title: 'AÇÕES',
            key: 'actions',
            width: '10%',
            render: (record) => (
                <div className="d-flex justify-content-center gap-3">
                    {record.isNew && <WarningOptionComp />}
                    <i
                        className="fa-solid fa-trash fs-6 text-danger"
                        style={{ cursor: 'pointer' }}
                        title="Excluir este arquivo"
                        onClick={() => handleDeleteFile(record)}
                    />
                </div>
            )
        }
    ];

    return (
        <div>
            <Modal
                getContainer={false}
                open={visible}
                onCancel={handleClose}
                width="60%"
                footer={null}
                centered
                title={"Upload de Planos de Ação"}
            >
                <div className="text-center">
                    <p className="fs-6 mb-1">
                        <strong>Formatos permitidos:</strong> Word, Excel, PowerPoint, PDF, ZIP.
                    </p>
                    <p className="fs-6 mb-1">
                        <strong>Tamanho máximo:</strong> 50MB por arquivo.
                    </p>
                    <p className="fs-6 mb-1">
                        <WarningOptionComp /> Este ícone, quando exibido, indica que o documento ainda não foi salvo.
                    </p>

                    <div className="d-flex justify-content-center gap-2 my-3">
                        <ButtonComp
                            title="ADICIONAR ARQUIVO"
                            onClick={openFileDialog}
                            buttonColor="#2b97be"
                            classnameIcon="fa-solid fa-upload"
                            hoverMessage="Adicionar arquivo"
                            type="button"
                        />
                        <ButtonComp
                            title="ATUALIZAR"
                            onClick={handleClearFiles}
                            buttonColor="#125578"
                            classnameIcon="fa-solid fa-rotate"
                            hoverMessage="Atualizar/Limpar arquivos ainda não salvos"
                            type="button"
                        />
                    </div>
                </div>

                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept={allowedExtensions.join(",")}
                    multiple
                    onChange={handleAddFiles}
                />

                <div className='mb-0'>
                    <ConfigProvider locale={ptBR}>
                        <StyledTable
                            columns={columns}
                            dataSource={documentosState}
                            rowKey={(record) => record.id || record.tempId}
                            loading={{
                                spinning: loading,
                                indicator: <Spinner />,
                            }}
                            pagination={{
                                pageSize: 5,
                                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
                            }}
                            scroll={{ y: 300, x: 'max-content' }}
                            locale={{
                                emptyText: <h3 className='m-3'> Nenhum documento disponível para exibição. </h3>
                            }}
                        />
                    </ConfigProvider>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4">
                    <ButtonComp
                        title="ANEXAR"
                        onClick={handleSubmit}
                        buttonColor="#2b97be"
                        classnameIcon="fa-solid fa-floppy-disk"
                        hoverMessage="Salvar alterações"
                        type="button"
                    />
                    <ButtonComp
                        title="CANCELAR"
                        onClick={handleClose}
                        buttonColor="#dc3545"
                        classnameIcon="fa-solid fa-times"
                        hoverMessage="Cancelar"
                        type="button"
                    />
                </div>

            </Modal>
        </div>
    );
};

export default PlanoAcaoAnexoModal;